import React, { useState, useEffect } from "react";
import notificationStore from "../../stores/Notification/notificationStore";
import useStore from "../../helpers/useStore";
import { useApi } from "../../api/ApiProvider";
import {
  DocumentDto,
  UploadDocumentMetadataDto,
  DocumentDtoDocuTypeEnum,
} from "../../api-client";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  List,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  Autocomplete,
} from "@mui/material";
import DocumentUpload from "../DocumentUpload/DocumentUpload";
import DocumentListItem from "./DocumentListItem";
import { isValidBase64 } from "../../utils/DocumentUtils";
import SimpleDocumentViewer from "./SimpleDocumentViewer";
import { PatientsContext } from "../../stores/Patients/patients.provider";

// DocumentListViewer Component
export const DocumentListViewer = () => {
  const PatientStore = useStore(PatientsContext);
  const { selectedPatientId:patientId } = PatientStore;

  // Document type options from enum
  const documentTypeOptions = Object.values(DocumentDtoDocuTypeEnum);

  const fileMetaData: UploadDocumentMetadataDto = {
    docuType: "Medizinisch",
    ownerType: "Patient",
    ownerId: patientId as unknown as string,
    makeAccessible: true,
  };

  // API hooks for document operations
  const { documents: documentsApi } = useApi();

  // State variables
  const [documents, setDocuments] = useState<DocumentDto[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accessUrl, setAccessUrl] = useState<string>();
  const [secureViewerOpen, setSecureViewerOpen] = useState(false);
  const [secureDocumentData, setSecureDocumentData] = useState<{
    mimeType: string;
    base64Data: string;
    name: string;
  } | null>(null);
  const [initialDocumentId, setInitialDocumentId] = useState<string | undefined>(undefined);
  
  // State variables for edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState<DocumentDto | null>(null);
  const [editDocumentName, setEditDocumentName] = useState("");
  const [viewerPosition, setViewerPosition] = useState<'default' | 'right-third'>('default');
  const [editDocumentType, setEditDocumentType] = useState("");
  
  // State variables for delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentDto | null>(null);

  // useEffect to fetch documents when the component mounts or patientId changes
  useEffect(() => {
    if (!patientId) return;

    // Function to fetch documents
    const fetchDocuments = async () => {
      setLoading(true);
      try {
        const docs = await documentsApi.getDocuments(patientId);
        setDocuments(docs as DocumentDto[]);
      } catch (err) {
        notificationStore.showMessage("Fehler beim Abruf der Dokumente", "error", err);
      } finally {
        setLoading(false);
      }
    };

    // Call the fetch function
    fetchDocuments();
  }, [patientId, documentsLoading]);

  useEffect(() => {
    if (accessUrl) {
      window.open(accessUrl, "_blank");
    }
  }, [accessUrl]);

  // Handle document download (traditional way)
  const handleDocumentDownload = (document: DocumentDto) => {
    const fetchAccess = async () => {
      try {
        const response = await documentsApi.requestAccessForDocument(document.id);
        setAccessUrl((response as any).accessUrl);
      } catch (err) {
        notificationStore.showMessage("Zugriff auf Dokument ist fehlgeschlagen", "error", err);
      }
    };

    fetchAccess();
  };

  // Handle secure document viewing
  const handleSecureDocumentView = async (document: DocumentDto) => {
    try {
      // Set the document ID that should be initially loaded
      setInitialDocumentId(document.id);
      // Open the viewer
      setSecureViewerOpen(true);
    } catch (err) {
      console.error('Document view error:', err);
      notificationStore.showMessage("Sichere Anzeige des Dokuments fehlgeschlagen", "error", err);
    }
  };

  const handleCloseSecureViewer = () => {
    setSecureViewerOpen(false);
    setSecureDocumentData(null);
    setViewerPosition('default'); // Reset position when closing
    setInitialDocumentId(undefined); // Reset initial document ID
  };

  // Handle document edit
  const handleEditDocument = (document: DocumentDto) => {
    setCurrentDocument(document);
    setEditDocumentName(document.label || document.name?.split('/').pop() || "");
    setEditDocumentType(document.docuType || "");
    setEditDialogOpen(true);
  };

  // Handle document edit save
  const handleSaveEdit = async () => {
    if (!currentDocument) return;

    try {
      // Use the API client's updateDocument method
      await documentsApi.updateDocument(currentDocument.id, {
        label: editDocumentName,
        docuType: editDocumentType as DocumentDtoDocuTypeEnum,
      });

      // Update documents list with type-safe approach
      setDocuments(
        documents.map((doc) =>
          doc.id === currentDocument.id
            ? { ...doc, name: editDocumentName, docuType: editDocumentType as DocumentDtoDocuTypeEnum }
            : doc
        )
      );

      notificationStore.showMessage("Dokument erfolgreich aktualisiert", "success");
    } catch (err) {
      notificationStore.showMessage("Fehler beim Aktualisieren des Dokuments", "error", err);
    } finally {
      setEditDialogOpen(false);
      setCurrentDocument(null);
    }
  };

  // Handle document delete
  const handleDeleteDocument = (document: DocumentDto) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  };

  // Handle document delete confirm
  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;

    try {
      // Use the API client's deleteDocument method
      await documentsApi.deleteDocument(documentToDelete.id);

      // Update documents list
      setDocuments(documents.filter((doc) => doc.id !== documentToDelete.id));

      notificationStore.showMessage("Dokument erfolgreich gelöscht", "success");
    } catch (err) {
      notificationStore.showMessage("Fehler beim Löschen des Dokuments", "error", err);
    } finally {
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  return (
    <>
      <Card>
      <CardHeader
        title="Documente"
        action={
          <DocumentUpload
            documentMetaData={fileMetaData}
            setDocumentsLoading={setDocumentsLoading}
            documentsLoading={documentsLoading}
          />
        }
      />
      <CardContent>
        {/* Loading state */}
        {loading && <p>Dokumente werden geladen ...</p>}

        {/* No documents state */}
        {!loading && documents.length === 0 && <p>Keine Dokumente vorhanden.</p>}

            {/* Documents list */}
            <List>
              {documents.map((document: DocumentDto, index) => (
                <DocumentListItem
                  key={document.id || index}
                  document={document}
                  index={index}
                  onSecureView={handleSecureDocumentView}
                  onEdit={handleEditDocument}
                  onDelete={handleDeleteDocument}
                />
              ))}
            </List>
      </CardContent>
    </Card>

      {/* Secure Document Viewer */}
      <SimpleDocumentViewer
        open={secureViewerOpen}
        onClose={handleCloseSecureViewer}
        patientId={patientId}
        position={viewerPosition}
        onPositionChange={setViewerPosition}
        initialDocumentId={initialDocumentId}
      />

      {/* Edit Document Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        aria-labelledby="edit-document-dialog-title"
        aria-describedby="edit-document-dialog-description"
      >
        <DialogTitle id="edit-document-dialog-title">Dokument bearbeiten</DialogTitle>
        <DialogContent>
          <DialogContentText id="edit-document-dialog-description">
            Bearbeite die Dokumentdetails.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="document-name"
            label="Dokumentname"
            type="text"
            fullWidth
            variant="standard"
            value={editDocumentName}
            onChange={(e) => setEditDocumentName(e.target.value)}
          />
          <Autocomplete
            freeSolo
            options={documentTypeOptions}
            value={editDocumentType}
            onChange={(event, newValue) => {
              setEditDocumentType(newValue || '');
            }}
            onInputChange={(event, newInputValue) => {
              setEditDocumentType(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Dokumenttyp"
                variant="standard"
                fullWidth
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleSaveEdit} color="primary">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Document Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-document-dialog-title"
        aria-describedby="delete-document-dialog-description"
      >
        <DialogTitle id="delete-document-dialog-title">Dokument löschen</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-document-dialog-description">
            Sind Sie sicher, dass Sie dieses Dokument löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleConfirmDelete} color="primary">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
